import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, RefreshCw } from 'lucide-react';
import Layout from '../../../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../../../components/DataGrid/DataGrid';
import {
  tillSessionService,
  TillSessionListDTO,
  TillSessionStatus,
} from '../../../../services/tillSessionService';
import './TillPage.css';

// ─── Badge map ────────────────────────────────────────────────────────────────

const STATUS_BADGE: Record<TillSessionStatus, string> = {
  Open:   'ims-badge ims-badge-approved',
  Closed: 'ims-badge ims-badge-completed',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtCurrency(amount?: number | null): string {
  if (amount === null || amount === undefined) return '—';
  return amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDatetime(iso?: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
}

// ─── Component ────────────────────────────────────────────────────────────────

const TillHistoryPage: React.FC = () => {
  const { t }    = useTranslation();
  const navigate = useNavigate();

  const [sessions, setSessions]   = useState<TillSessionListDTO[]>([]);
  const [loading,  setLoading]    = useState(false);
  const [error,    setError]      = useState<string | null>(null);
  const [total,    setTotal]      = useState(0);
  const [page,     setPage]       = useState(1);

  // Filters
  const [fromDate, setFromDate] = useState('');
  const [toDate,   setToDate]   = useState('');

  const pageSize = 20;

  const loadSessions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await tillSessionService.getHistory({
        pageNumber: page,
        pageSize,
        from: fromDate || undefined,
        to:   toDate   || undefined,
      });
      // PaginatedResponse lands in res.data directly (matches ApiResponse shape)
      const body = res.data as any;
      if (body?.data) {
        setSessions(body.data as TillSessionListDTO[]);
        setTotal(body.pagination?.total ?? 0);
      } else {
        setSessions([]);
        setTotal(0);
      }
    } catch {
      setError(t('till.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [page, fromDate, toDate, t]);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  // ─── Grid config ────────────────────────────────────────────────────────────

  const columns: GridColumn[] = [
    {
      key: 'sessionNumber',
      label: t('till.sessionNumber'),
      sortable: true,
      width: '160px',
    },
    {
      key: 'cashierName',
      label: t('till.cashier'),
      sortable: true,
      width: '160px',
    },
    {
      key: 'openedAt',
      label: t('till.openedAt'),
      sortable: true,
      width: '140px',
      render: (v: string) => fmtDatetime(v),
    },
    {
      key: 'closedAt',
      label: t('till.closedAt'),
      sortable: true,
      width: '140px',
      render: (v?: string) => fmtDatetime(v),
    },
    {
      key: 'status',
      label: t('till.status'),
      width: '90px',
      render: (v: TillSessionStatus) => (
        <span className={STATUS_BADGE[v] ?? 'ims-badge'}>
          {t(`till.status${v}`)}
        </span>
      ),
    },
    {
      key: 'openingBalance',
      label: t('till.openingBalance'),
      width: '120px',
      render: (v: number) => fmtCurrency(v),
    },
    {
      key: 'expectedClosingBalance',
      label: t('till.expectedClosingBalance'),
      width: '130px',
      hideMobile: true,
      render: (v?: number) => fmtCurrency(v),
    },
    {
      key: 'declaredClosingBalance',
      label: t('till.declaredClosingBalance'),
      width: '130px',
      hideMobile: true,
      render: (v?: number) => fmtCurrency(v),
    },
    {
      key: 'variance',
      label: t('till.variance'),
      width: '110px',
      render: (v?: number) => {
        if (v === null || v === undefined) return '—';
        const cls =
          Math.abs(v) <= 0.01 ? 'till-variance--ok'
          : v > 0             ? 'till-variance--over'
          :                     'till-variance--short';
        return <span className={cls}>{fmtCurrency(v)}</span>;
      },
    },
  ];

  const actions: GridAction[] = [
    {
      label: t('common.view'),
      icon: <Eye size={16} strokeWidth={2} />,
      onClick: (row: TillSessionListDTO) => navigate(`/sales/till/${row.id}`),
      className: 'grid-action-btn-view',
      title: t('common.view'),
    },
  ];

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <Layout>
      <div className="ims-page till-page">

        {/* Header */}
        <div className="ims-list-header">
          <div>
            <h1 className="ims-list-title">{t('till.historyTitle')}</h1>
            <p className="ims-page-subtitle">{t('till.historySubtitle')}</p>
          </div>
          <div className="ims-list-actions">
            <button className="btn-refresh" onClick={loadSessions} disabled={loading}>
              <RefreshCw size={14} strokeWidth={2} className={loading ? 'refresh-icon-spinning' : ''} />
              {loading ? t('common.loading') : t('common.refresh')}
            </button>
            <button className="ims-btn ims-btn-primary" onClick={() => navigate('/sales/till/open')}>
              {t('till.openTill')}
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="till-filters">
          <div className="till-filter-group">
            <label className="till-filter-label">{t('till.filterFromDate')}</label>
            <input
              type="date"
              className="till-filter-input"
              value={fromDate}
              onChange={e => { setFromDate(e.target.value); setPage(1); }}
            />
          </div>
          <div className="till-filter-group">
            <label className="till-filter-label">{t('till.filterToDate')}</label>
            <input
              type="date"
              className="till-filter-input"
              value={toDate}
              onChange={e => { setToDate(e.target.value); setPage(1); }}
            />
          </div>
          {(fromDate || toDate) && (
            <button
              className="ims-btn ims-btn-ghost"
              style={{ alignSelf: 'flex-end' }}
              onClick={() => { setFromDate(''); setToDate(''); setPage(1); }}
            >
              {t('common.clearFilters')}
            </button>
          )}
        </div>

        {error && <div className="ims-alert ims-alert-error" style={{ marginBottom: 12 }}>{error}</div>}

        {/* Grid */}
        <div className="ims-grid-wrapper">
          <DataGrid
            columns={columns}
            data={sessions}
            actions={actions}
            loading={loading}
            emptyMessage={t('till.noSessions')}
            pageSize={pageSize}
          />
        </div>

        {total > pageSize && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 12 }}>
            <button
              className="ims-btn ims-btn-ghost"
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
            >
              ‹ {t('common.previous')}
            </button>
            <span style={{ lineHeight: '34px', fontSize: 13, color: 'var(--ims-text-sub)' }}>
              {page} / {Math.ceil(total / pageSize)}
            </span>
            <button
              className="ims-btn ims-btn-ghost"
              disabled={page * pageSize >= total}
              onClick={() => setPage(p => p + 1)}
            >
              {t('common.next')} ›
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default TillHistoryPage;
